import React, { useRef, useCallback } from 'react';
import { useChatContext } from '../hooks/ChatContext.jsx';

const ChatSidebar = () => {
  const {
    conversations,
    loadingConversations,
    conversationsError,
    activeConversation,
    setActiveConversation,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useChatContext();

  const listRef = useRef(null);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || !hasNextPage || isFetchingNextPage) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return (
    <aside className="w-64 h-full flex flex-col bg-background/80 dark:bg-background/60 backdrop-blur-md shadow-lg rounded-r-2xl border-r border-border/30 dark:border-border/20 transition-colors duration-300">
      <div className="px-6 py-5 select-none">
        <h1 className="text-3xl font-extrabold tracking-tight flex items-center gap-1">
          <span className="text-primary drop-shadow-sm">Aurora</span>
          <span className="bg-gradient-to-r from-fuchsia-500 via-sky-500 to-emerald-400 bg-clip-text text-transparent animate-gradient-move">
            Ai
          </span>
        </h1>
      </div>
      {loadingConversations && (
        <div className="px-6 py-4 text-muted-foreground">Ladataan...</div>
      )}
      {conversationsError && (
        <div className="px-6 py-4 text-destructive">Virhe keskustelujen haussa</div>
      )}
      <ul
        className="flex-1 overflow-y-auto px-2 pb-2 space-y-2"
        ref={listRef}
        onScroll={handleScroll}
        style={{ minHeight: 0 }}
      >
        {conversations.map((conv, idx) => {
          let title = 'Uusi keskustelu';
          let dateStr = '';
          if (conv.messages && conv.messages.length > 0) {
            const firstMsg = conv.messages[0];
            title = firstMsg.content
              ? firstMsg.content.slice(0, 40) + (firstMsg.content.length > 40 ? '...' : '')
              : 'Uusi keskustelu';
            const lastMsg = conv.messages[conv.messages.length - 1];
            if (lastMsg.timestamp) {
              const d = new Date(lastMsg.timestamp);
              dateStr = d.toLocaleDateString('fi-FI', { day: '2-digit', month: '2-digit', year: '2-digit' }) + ' ' + d.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
            }
          }
          return (
            <li
              key={conv.id || idx}
              className={`group rounded-xl transition-all cursor-pointer px-4 py-3 flex flex-col gap-1 select-none
                ${activeConversation?.id === conv.id
                  ? 'bg-primary/10 dark:bg-primary/20 ring-primary/30 dark:ring-primary/40 text-primary font-semibold shadow-sm'
                  : 'hover:bg-muted/60 dark:hover:bg-muted/40 text-foreground/80'}
              `}
              onClick={async () => await setActiveConversation(conv)}
            >
              <div className="truncate text-base leading-tight">{title}</div>
              {dateStr && <div className="text-xs text-muted-foreground mt-0.5">{dateStr}</div>}
            </li>
          );
        })}
        {isFetchingNextPage && (
          <li className="px-4 py-3 text-muted-foreground text-center">Ladataan lisää...</li>
        )}
      </ul>
      <div className="p-4 mt-2">
        <button
          className="w-full py-2 rounded-xl bg-primary text-primary-foreground font-medium shadow hover:bg-primary/90 transition-colors duration-200"
          onClick={() => setActiveConversation(null)}
        >
          + Uusi keskustelu
        </button>
      </div>
    </aside>
  );
};

export default ChatSidebar; 